import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { ContactForm } from "../components/ContactForm";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FaqSection } from "../sections/FaqSection";
import { Testimonials } from "../sections/Testimonials";
import { useUI } from "../context/UIContext";
import { IMAGES } from "../data/content";

export default function SegmentPage({ config }) {
  const { openUpload } = useUI();

  return (
    <>
      <Seo title={config.seoTitle} description={config.subtitle} path={config.path} image={config.image} />
      <PageHero
        eyebrow={config.eyebrow}
        titleLines={config.titleLines}
        subtitle={config.subtitle}
        image={config.image}
        breadcrumb={[{ label: config.eyebrow }]}
      />

      <section className="bg-background py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="text-2xl sm:text-3xl font-heading font-semibold text-navy-900 leading-snug mb-10">
                  {config.intro}
                </p>
              </Reveal>
              <div className="space-y-4">
                {config.points.map((p, i) => (
                  <Reveal key={i} delay={i * 0.06}>
                    <div className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-300 transition-colors" data-testid={`segment-point-${i}`}>
                      <span className="shrink-0 inline-grid place-items-center h-10 w-10 rounded-xl bg-blue-50 text-blue-700">
                        <Check className="h-5 w-5" strokeWidth={2.5} />
                      </span>
                      <div>
                        <h3 className="font-heading text-lg text-navy-900">{p.title}</h3>
                        <p className="text-slate-500 text-sm mt-1 leading-relaxed">{p.text}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="lg:sticky lg:top-28 space-y-6"
              >
                <div className="rounded-3xl overflow-hidden border border-slate-200">
                  <img src={IMAGES[config.sideImage]} alt={config.eyebrow} loading="lazy" className="w-full h-56 object-cover" />
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.15)]">
                  <h3 className="text-2xl font-heading font-semibold text-navy-900 mb-2">
                    Kostenlose <span className="font-serif italic text-blue-700">Analyse</span>
                  </h3>
                  <p className="text-sm text-slate-500 mb-6">Persönliche Einschätzung in 24 Stunden.</p>
                  <button
                    onClick={openUpload}
                    className="w-full group rounded-full bg-blue-700 hover:bg-blue-600 text-white py-4 font-medium transition-colors flex items-center justify-center gap-2 mb-5"
                    data-testid="segment-upload-btn"
                  >
                    Rechnung hochladen
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                  <ContactForm compact topic={config.topic} testid={`segment-form-${config.topic}`} />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Testimonials />
      <FaqSection />
    </>
  );
}
